import {
  createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  useSyncExternalStore, type ComponentProps, type CSSProperties, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { WorkspaceActionFormContext } from './workspace-action-context';
import type { WorkspaceHistory } from './workspace-edit-history';
export { useWorkspaceEditHistory, equalWorkspaceValue } from './workspace-edit-history';
export type { WorkspaceHistory, WorkspaceEditHistory, WorkspaceHistorySnapshot } from './workspace-edit-history';
export { useWorkspaceFormDraft } from './workspace-form-history';
import './workspace-actions.css';

export interface WorkspaceDraftState {
  dirty: boolean;
  pending?: boolean;
  /**
   * The feature reconciles its own draft. A rejection keeps its lock; a
   * `false` result means the owner already reported why and the task stays open.
   */
  onDiscard?: () => void | boolean | Promise<void | boolean>;
  /** The draft is kept elsewhere (wizard autosave): closing the task keeps it without asking. */
  persisted?: boolean;
  history?: WorkspaceHistory;
}

/** How a task's X/Escape behaves: close, confirm a discard first, or wait. */
export type WorkspaceDismissal = 'clean' | 'dirty' | 'pending';

interface Scope {
  id: string;
  host: string;
  depth: number;
  kind: 'page' | 'panel';
  enabled: boolean;
  active: boolean;
}
interface Claim extends Scope {
  token: symbol;
  order: number;
  actions: boolean;
  visible: boolean;
  dirty: boolean;
  pending: boolean;
  persisted: boolean;
  onDiscard?: WorkspaceDraftState['onDiscard'];
  history?: WorkspaceHistory;
}
interface Outlet { node: HTMLDivElement; owner: symbol }
const pageScope: Scope = { id: 'page', host: 'page', depth: 0, kind: 'page', enabled: true, active: true };
const ScopeContext = createContext<Scope>(pageScope);

/** A stable external registry: keystrokes do not rerender the shell or providers. */
function createActionsStore() {
  const claims = new Map<symbol, Claim>();
  const outlets = new Map<string, Outlet>();
  const listeners = new Set<() => void>();
  const sources = new Map<symbol, HTMLElement>();
  let order = 0;
  let noticeHost: string | null = null;
  let notice = '';
  // Each blocked attempt counts, so a repeated click explains itself again.
  let noticeAttempt = 0;
  const emit = () => {
    if (![...claims.values()].some(claim => claim.enabled && (claim.dirty || claim.pending))) { noticeHost = null; notice = ''; }
    listeners.forEach(listener => listener());
  };
  const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
  const select = (host: string) => [...claims.values()]
    .filter(claim => claim.host === host && claim.enabled && claim.active && claim.actions && claim.visible)
    .sort((a, b) => b.depth - a.depth || b.order - a.order)[0]?.token ?? null;
  const isBlocked = (host?: string) => [...claims.values()].some(claim => claim.enabled && (!host || claim.host === host) && (claim.dirty || claim.pending));
  const getNavigationBlockedSnapshot = () => isBlocked();
  // Dismissal is per host: a nested task's draft never decides its parent's X.
  const unsaved = (host: string) => [...claims.values()].filter(claim => claim.enabled && claim.host === host && claim.dirty && !claim.persisted);
  const dismissal = (host: string): WorkspaceDismissal => [...claims.values()].some(claim => claim.enabled && claim.host === host && claim.pending)
    ? 'pending' : unsaved(host).length ? 'dirty' : 'clean';
  return {
    subscribe, select, isBlocked, getNavigationBlockedSnapshot, dismissal,
    /** Runs each unsaved draft's own discard; `false` keeps the task open. */
    async discard(host: string) {
      for (const claim of unsaved(host)) if (await claim.onDiscard?.() === false) return false;
      return true;
    },
    navigationNotice: (host: string) => noticeHost === host ? notice : '',
    navigationAttempt: (host: string) => noticeHost === host ? noticeAttempt : 0,
    history(host: string, id: string, own?: WorkspaceHistory) {
      return own ?? [...claims.values()].filter(claim => claim.host === host && claim.id === id && claim.enabled && claim.active && claim.history)
        .sort((a, b) => b.order - a.order)[0]?.history;
    },
    pending(host: string, id: string) {
      return [...claims.values()].some(claim => claim.enabled && claim.host === host && claim.id === id && claim.pending);
    },
    attachSource(token: symbol, node: HTMLElement | null) {
      if (node) sources.set(token, node); else sources.delete(token);
    },
    editHistory(event: KeyboardEvent) {
      if (event.defaultPrevented || event.isComposing || event.altKey || !(event.metaKey || event.ctrlKey)) return;
      const key = event.key.toLowerCase();
      const redo = key === 'z' && event.shiftKey || key === 'y' && event.ctrlKey && !event.shiftKey;
      if (key !== 'z' && !redo) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-workspace-history-exclude], [data-slot="command-input"], input[type="search"]')) return;
      // Rich text editors retain their own selection-aware document history.
      if (target?.closest('[contenteditable="true"]') && !target.closest('[data-workspace-edit-history]')) return;
      const candidates = [...claims.values()].filter(claim => claim.enabled && claim.active)
        .sort((a, b) => b.depth - a.depth || b.order - a.order);
      // A foreground task prevents keyboard shortcuts from reaching the page.
      const foreground = candidates.find(claim => claim.kind === 'panel') ?? candidates[0];
      if (!foreground) return;
      const owner = candidates.find(claim => claim.host === foreground.host && claim.id === foreground.id && claim.history);
      if (!owner?.history) return;
      const ownerSources = candidates.filter(claim => claim.host === owner.host && claim.id === owner.id && claim.history === owner.history)
        .flatMap(claim => sources.get(claim.token) ?? []);
      const outlet = outlets.get(owner.host)?.node;
      if (target && ownerSources.length && !ownerSources.some(source => source.contains(target)) && !outlet?.contains(target)) return;
      if ([...claims.values()].some(claim => claim.enabled && claim.host === owner.host && claim.id === owner.id && claim.pending)) {
        event.preventDefault(); event.stopPropagation(); return;
      }
      // Prevent the native input undo stack from diverging from the form owner.
      if (redo ? owner.history.canRedo : owner.history.canUndo) {
        event.preventDefault(); event.stopPropagation(); (redo ? owner.history.redo : owner.history.undo)();
      } else { event.preventDefault(); event.stopPropagation(); }
    },
    /** ⌘S / Ctrl+S: submit the foreground island's enabled primary. The browser's Save Page never opens over an island. */
    saveShortcut(event: KeyboardEvent) {
      if (event.defaultPrevented || event.isComposing || event.altKey || event.shiftKey || !(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 's') return;
      const candidates = [...claims.values()].filter(claim => claim.enabled && claim.active && claim.actions && claim.visible)
        .sort((a, b) => b.depth - a.depth || b.order - a.order);
      const foreground = candidates.find(claim => claim.kind === 'panel') ?? candidates[0];
      const outlet = foreground && outlets.get(foreground.host);
      if (!foreground || !outlet || outlet.owner !== select(foreground.host)) return;
      // A dialog over the panel (quick create, confirm) owns its own keys.
      const target = event.target instanceof Element ? event.target : null;
      const dialog = target?.closest('[role="dialog"], [role="alertdialog"]');
      if (dialog && !dialog.contains(outlet.node)) return;
      event.preventDefault();
      const primary = [...outlet.node.querySelectorAll<HTMLElement>(primaryCommand)].filter(node => node instanceof HTMLButtonElement).at(-1) as HTMLButtonElement | undefined;
      if (primary && !primary.disabled && primary.getAttribute('aria-disabled') !== 'true') primary.click();
    },
    register(token: symbol, value: Omit<Claim, 'token' | 'order'>) {
      const previous = claims.get(token);
      claims.set(token, { ...value, token, order: previous?.order ?? ++order });
      emit();
      return () => { if (claims.delete(token)) emit(); };
    },
    outlet: (host: string) => outlets.get(host) ?? null,
    attachOutlet(host: string, outlet: Outlet) {
      outlets.set(host, outlet); emit();
      return () => { if (outlets.get(host) === outlet) { outlets.delete(host); emit(); } };
    },
    focusBlocked() {
      const blocked = [...claims.values()].filter(claim => claim.enabled && (claim.dirty || claim.pending))
        .sort((a, b) => Number(b.active) - Number(a.active) || b.depth - a.depth || b.order - a.order)[0];
      if (!blocked) return;
      const foreground = blocked.active ? blocked : [...claims.values()]
        .filter(claim => claim.enabled && claim.active && claim.actions && claim.visible)
        .sort((a, b) => b.depth - a.depth || b.order - a.order)[0] ?? blocked;
      const node = outlets.get(foreground.host)?.node;
      noticeHost = foreground.host;
      notice = blocked.pending ? 'Wait for this operation to finish before leaving.' : 'Save or discard your changes before leaving.';
      noticeAttempt += 1;
      emit();
      if (node) {
        // Narrow islands keep overflowed commands in the DOM but not on screen.
        const shown = (button: HTMLButtonElement) => typeof button.checkVisibility !== 'function' || button.checkVisibility();
        const buttons = [...node.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')].filter(shown);
        const button = buttons.find(candidate => candidate.dataset.panelAction !== 'icon') ?? buttons[0];
        if (button) button.focus({ preventScroll: true });
        else (node.closest<HTMLElement>('.workspace-action-island') ?? node).focus({ preventScroll: true });
      }
    },
  };
}
type ActionsStore = ReturnType<typeof createActionsStore>;
const StoreContext = createContext<ActionsStore | null>(null);

export function WorkspaceActionsProvider({ children }: { children: ReactNode }) {
  const parent = useContext(StoreContext);
  return parent ? children : <ActionsRoot>{children}</ActionsRoot>;
}
function ActionsRoot({ children }: { children: ReactNode }) {
  const [store] = useState(createActionsStore);
  // Document navigation is an external browser boundary, shared by standalone
  // editors and router hosts. SPA/history navigation is guarded by its adapter.
  useLayoutEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!store.isBlocked()) return;
      event.preventDefault(); event.returnValue = '';
    };
    const visit = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !store.isBlocked()) return;
      const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
      if (!anchor || anchor.hasAttribute('download') || anchor.target && anchor.target !== '_self') return;
      const destination = new URL(anchor.href, window.location.href);
      if (destination.href === window.location.href || destination.origin === window.location.origin && destination.pathname === window.location.pathname && destination.search === window.location.search && anchor.getAttribute('href')?.startsWith('#')) return;
      // Feature handlers may open their existing discard/reconciliation screen.
      // The native adapter still guards any actual programmatic route change.
      event.preventDefault(); store.focusBlocked();
    };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', visit, true);
    document.addEventListener('keydown', store.editHistory, true);
    document.addEventListener('keydown', store.saveShortcut);
    return () => { window.removeEventListener('beforeunload', beforeUnload); document.removeEventListener('click', visit, true); document.removeEventListener('keydown', store.editHistory, true); document.removeEventListener('keydown', store.saveShortcut); };
  }, [store]);
  return <StoreContext value={store}>{children}</StoreContext>;
}

export function useWorkspaceActionsStore() {
  const store = useContext(StoreContext);
  if (!store) throw new Error('Workspace actions require a WorkspaceActionsProvider or WorkspacePanelProvider');
  return store;
}
/** Check before identity changes or other side effects that would unmount a draft. */
export function useWorkspaceNavigationCheck() {
  const store = useContext(StoreContext);
  return useCallback(() => {
    if (!store?.isBlocked()) return true;
    store.focusBlocked();
    // A selecting menu restores its opener as it closes. Return focus to the
    // live commands after that focus scope has released the editor.
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => {
      if (store.isBlocked()) store.focusBlocked();
    });
    return false;
  }, [store]);
}
export function useWorkspaceActionsNavigationBlocked() {
  const store = useWorkspaceActionsStore();
  return useSyncExternalStore(store.subscribe, store.getNavigationBlockedSnapshot, () => false);
}
export function useWorkspaceActionScopeBlocked(host?: string) {
  const scope = useContext(ScopeContext);
  const store = useWorkspaceActionsStore();
  return useSyncExternalStore(store.subscribe, () => store.isBlocked(host ?? scope.host), () => false);
}

/** Panels get their own focus-safe outlet; embedded editors inherit the host. */
export function WorkspaceActionScope({ id, kind, enabled = true, active = true, children }: {
  id?: string; kind?: Scope['kind']; enabled?: boolean; active?: boolean; children: ReactNode;
}) {
  const parent = useContext(ScopeContext);
  const generated = useId();
  const scopeId = id ?? generated;
  const value = useMemo<Scope>(() => ({
    id: scopeId, host: kind ? scopeId : parent.host, depth: parent.depth + 1,
    kind: kind ?? parent.kind, enabled: parent.enabled && enabled, active: kind ? active : parent.active && active,
  }), [scopeId, kind, parent, enabled, active]);
  return <ScopeContext value={value}>{children}</ScopeContext>;
}

export function useWorkspaceDismissal(host?: string) {
  const scope = useContext(ScopeContext);
  const store = useWorkspaceActionsStore();
  return useSyncExternalStore(store.subscribe, () => store.dismissal(host ?? scope.host), () => 'clean' as const);
}

function useClaim({ actions, visible, dirty = false, pending = false, persisted = false, onDiscard, history }: {
  actions: boolean; visible: boolean; dirty?: boolean; pending?: boolean; persisted?: boolean; onDiscard?: WorkspaceDraftState['onDiscard']; history?: WorkspaceHistory;
}) {
  const store = useWorkspaceActionsStore();
  const scope = useContext(ScopeContext);
  const [token] = useState(() => Symbol('workspace-action-owner'));
  // Features pass inline discard handlers; the registry keeps one stable caller.
  const discardRef = useRef(onDiscard);
  useLayoutEffect(() => { discardRef.current = onDiscard; });
  const canDiscard = Boolean(onDiscard);
  const discard = useCallback(() => discardRef.current?.(), []);
  // This effect synchronizes feature-owned flags with the external registry.
  // Registration never owns form data or submission, and cleanup is token-owned.
  useLayoutEffect(() => store.register(token, { ...scope, actions, visible, dirty, pending, persisted, onDiscard: canDiscard ? discard : undefined, history }),
    [store, token, scope, actions, visible, dirty, pending, persisted, canDiscard, discard, history]);
  return { store, scope, token };
}

/** Register a draft guard when its commands are composed elsewhere. */
export function WorkspaceDraft({ dirty, pending, persisted, onDiscard, history }: WorkspaceDraftState) {
  const { store, token } = useClaim({ actions: false, visible: false, dirty, pending, persisted, onDiscard, history });
  const attach = useCallback((node: HTMLSpanElement | null) => {
    store.attachSource(token, node?.closest<HTMLElement>('form, [data-slot="workspace-panel"]') ?? node?.parentElement ?? null);
    return () => store.attachSource(token, null);
  }, [store, token]);
  return <span hidden ref={attach} data-workspace-draft-owner />;
}

export interface WorkspaceSaved {
  /** A short context after "Saved", e.g. "12 properties updated". */
  context?: string;
}
/** How long the island holds "✓ Saved" before it returns to its commands or leaves. */
export const WORKSPACE_SAVED_MS = 1200;

export interface WorkspaceActionsProps extends Omit<WorkspaceDraftState, 'dirty'> {
  dirty?: boolean;
  children: ReactNode;
  visible?: boolean;
  formId?: string;
  label?: string;
  /**
   * Set a new object after a successful save: the island morphs into "✓ Saved"
   * for {@link WORKSPACE_SAVED_MS}, then shows its commands again, or leaves when
   * it is otherwise hidden (a clean `visible={dirty || pending}` island).
   * A new edit (dirty) or a pending save ends it at once.
   */
  saved?: WorkspaceSaved | null;
}
/** Render the original commands in the active island, retaining React context. */
export function WorkspaceActions({ children, dirty = false, pending = false, persisted, onDiscard, visible, formId, label, history, saved }: WorkspaceActionsProps) {
  const currentScope = useContext(ScopeContext);
  const shown = visible ?? (currentScope.kind === 'panel' || dirty || pending);
  const [flash, setFlash] = useState<WorkspaceSaved | null>(null);
  const [seenSaved, setSeenSaved] = useState(saved);
  if (saved !== seenSaved) { setSeenSaved(saved); setFlash(saved ?? null); }
  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(null), WORKSPACE_SAVED_MS);
    return () => clearTimeout(timer);
  }, [flash]);
  const quiet = !dirty && !pending;
  const flashing = Boolean(flash && quiet);
  // A hidden island leaves still reading "Saved"; a shown one returns to its commands.
  const savedPill = Boolean(saved && quiet && (flashing || !shown));
  const { store, scope, token } = useClaim({ actions: true, visible: shown || flashing, dirty, pending, persisted, onDiscard, history });
  const [associatedForm, setAssociatedForm] = useState<string>();
  const generatedId = useId();
  const attach = useCallback((node: HTMLSpanElement | null) => {
    const form = formId ? document.getElementById(formId) as HTMLFormElement | null : node?.closest('form');
    store.attachSource(token, form ?? node?.closest<HTMLElement>('[data-slot="workspace-panel"]') ?? node?.parentElement ?? null);
    if (!form) return () => store.attachSource(token, null);
    // Association with the enclosing native form, never extraction of buttons.
    if (!form.id) form.id = `workspace-form-${generatedId}`;
    setAssociatedForm(form.id);
    return () => store.attachSource(token, null);
  }, [generatedId, store, token, formId]);
  const outlet = useSyncExternalStore(store.subscribe, () => store.outlet(scope.host), () => null);
  const activeHistory = useSyncExternalStore(store.subscribe, () => store.history(scope.host, scope.id, history), () => history);
  const ownerPending = useSyncExternalStore(store.subscribe, () => store.pending(scope.host, scope.id), () => pending);
  return <>
    <span ref={attach} hidden data-workspace-actions-owner={scope.id} />
    {outlet?.owner === token && createPortal(
      <WorkspaceActionFormContext value={formId ?? associatedForm}>
        <div className="workspace-action-commands" data-pending={pending} data-saved={savedPill || undefined} role="group" aria-label={label ?? 'Editor actions'}>
          {savedPill ? <WorkspaceSavedPill context={saved?.context} /> : <>
            {activeHistory && <WorkspaceHistoryActions history={activeHistory} pending={pending || ownerPending} />}{children}
          </>}
        </div>
      </WorkspaceActionFormContext>, outlet.node,
    )}
  </>;
}

function WorkspaceSavedPill({ context }: { context?: string }) {
  return <span className="workspace-action-saved" data-workspace-saved role="status">
    <span className="workspace-action-saved-mark" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
    </span>
    <span className="workspace-action-saved-label">Saved</span>
    {context && <span className="workspace-action-saved-context">{context}</span>}
  </span>;
}

function WorkspaceHistoryActions({ history, pending }: { history: WorkspaceHistory; pending: boolean }) {
  const snapshot = useSyncExternalStore(history.subscribe, history.getSnapshot, history.getSnapshot);
  const arrow = <><path d="m8 3-5 5 5 5" /><path d="M3 8h10a6 6 0 0 1 0 12h-3" /></>;
  return <div className="workspace-panel-history" role="group" aria-label="Edit history">
    <WorkspaceAction appearance="icon" aria-label="Undo" title="Undo (⌘/Ctrl Z)" disabled={pending || !snapshot.canUndo} onClick={history.undo}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{arrow}</svg>
    </WorkspaceAction>
    <WorkspaceAction appearance="icon" aria-label="Redo" title="Redo (⌘/Ctrl Shift Z)" disabled={pending || !snapshot.canRedo} onClick={history.redo}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><g transform="translate(24 0) scale(-1 1)">{arrow}</g></svg>
    </WorkspaceAction>
  </div>;
}

/** One island geometry for pages and panels: 6px inset, concentric pill controls. */
export function WorkspaceActionIsland({ className, ...props }: ComponentProps<'div'>) {
  const [keyboardNavigation, setKeyboardNavigation] = useState(false);
  // Menus restore focus programmatically. Capture actual navigation across
  // their portals and outside dismissals rather than treating that as input.
  useLayoutEffect(() => {
    const navigate = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey && !event.altKey &&
        (event.key === 'Tab' || event.key.startsWith('Arrow') || event.key === 'Home' || event.key === 'End')) setKeyboardNavigation(true);
    };
    const point = () => setKeyboardNavigation(false);
    document.addEventListener('keydown', navigate, true);
    document.addEventListener('pointerdown', point, true);
    return () => { document.removeEventListener('keydown', navigate, true); document.removeEventListener('pointerdown', point, true); };
  }, []);
  return <div {...props} className={cn('workspace-action-island workspace-panel-actions', className)} data-keyboard-navigation={keyboardNavigation} />;
}
export function WorkspaceAction({ appearance = 'secondary', className, ...props }: ComponentProps<typeof Button> & { appearance?: 'icon' | 'secondary' | 'primary' }) {
  return <Button type="button" variant="ghost" size={appearance === 'icon' ? 'iconSm' : 'xs'} {...props}
    className={cn('workspace-panel-action', className)} data-panel-action={appearance} />;
}

/*
 * Island slots. The primary command and the one before it always stay in the
 * row; on narrow panels history and every other command move into the "…"
 * sheet. `data-workspace-overflow` sends a command there on every size. The
 * sheet is the same DOM subtree, positioned by CSS, so form association,
 * submitters and React state are untouched.
 */
type IslandSlot = 'history' | 'primary' | 'secondary' | 'compact' | 'overflow';
const actionable = 'button, a[href], [role="button"]';
const primaryCommand = '[data-panel-action="primary"], [data-workspace-primary], [type="submit"], [data-variant="default"]';
function islandUnits(root: Element) {
  const units: HTMLElement[] = [];
  const visit = (parent: Element) => {
    for (const child of parent.children) {
      if (!(child instanceof HTMLElement) || child.hidden || child.matches('[data-workspace-actions-owner]')) continue;
      // "✓ Saved" is status, not a command: it never takes a slot or the overflow sheet.
      if (child.matches('[data-workspace-saved], [data-saved]')) { delete child.dataset.islandSlot; delete child.dataset.islandWrapper; continue; }
      const nested = child.querySelectorAll(actionable).length;
      if (child.matches('.workspace-panel-history, .workspace-panel-primary-group') || child.matches(actionable) || nested === 1) units.push(child);
      else if (nested > 1) { child.dataset.islandWrapper = ''; visit(child); }
      else if (child.textContent?.trim()) units.push(child);
    }
  };
  visit(root);
  return units;
}
function assignIslandSlots(root: Element) {
  const units = islandUnits(root);
  const slots = new Map<HTMLElement, IslandSlot>();
  const matches = (unit: HTMLElement, selector: string) => unit.matches(selector) || Boolean(unit.querySelector(selector));
  const rowUnits = units.filter(unit => {
    if (unit.matches('.workspace-panel-history')) { slots.set(unit, 'history'); return false; }
    if (matches(unit, '[data-workspace-overflow]')) { slots.set(unit, 'overflow'); return false; }
    return true;
  });
  const commands = rowUnits.filter(unit => matches(unit, actionable));
  const primary = [...commands].reverse().find(unit => matches(unit, primaryCommand)) ?? commands.at(-1);
  const secondary = primary ? commands[commands.indexOf(primary) - 1] : undefined;
  for (const unit of rowUnits) slots.set(unit, unit === primary ? 'primary' : unit === secondary ? 'secondary' : 'compact');
  let wide = 0;
  let narrow = 0;
  for (const unit of units) {
    const slot = slots.get(unit)!;
    if (unit.dataset.islandSlot !== slot) unit.dataset.islandSlot = slot;
    const inNarrow = slot !== 'primary' && slot !== 'secondary';
    unit.style.setProperty('--island-narrow-index', inNarrow ? String(narrow++) : '');
    unit.style.setProperty('--island-wide-index', slot === 'overflow' ? String(wide++) : '');
  }
  return { wide, narrow };
}

function useIslandOverflow(host: HTMLDivElement | null, island: HTMLDivElement | null) {
  const [counts, setCounts] = useState({ wide: 0, narrow: 0 });
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    if (!host) return;
    const update = () => {
      const next = assignIslandSlots(host);
      setCounts(current => current.wide === next.wide && current.narrow === next.narrow ? current : next);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled', 'type', 'data-workspace-overflow', 'data-variant'] });
    return () => observer.disconnect();
  }, [host]);
  useLayoutEffect(() => {
    if (!open || !island) return;
    const close = (restore: boolean) => {
      setOpen(false);
      if (restore) island.querySelector<HTMLElement>('.workspace-action-more')?.focus({ preventScroll: true });
    };
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !island.contains(event.target as Node | null)) return;
      event.preventDefault(); event.stopPropagation(); close(true);
    };
    const outside = (event: PointerEvent) => { if (!island.contains(event.target as Node | null)) close(false); };
    // A chosen command closes the sheet; history steps keep it open for repeats.
    const choose = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const unit = target?.closest<HTMLElement>('[data-island-slot]');
      if (unit && unit.dataset.islandSlot !== 'history' && target?.closest(actionable)) close(false);
    };
    document.addEventListener('keydown', key, true);
    document.addEventListener('pointerdown', outside, true);
    island.addEventListener('click', choose);
    return () => { document.removeEventListener('keydown', key, true); document.removeEventListener('pointerdown', outside, true); island.removeEventListener('click', choose); };
  }, [open, island]);
  // The sheet fits its longest row, within 200–280px, before it paints.
  useLayoutEffect(() => {
    if (!open || !island) return;
    island.setAttribute('data-sheet-measure', '');
    const rows = [...island.querySelectorAll<HTMLElement>('[data-island-slot]')].filter(row => getComputedStyle(row).position === 'absolute');
    const widest = Math.max(0, ...rows.map(row => row.offsetWidth));
    island.removeAttribute('data-sheet-measure');
    const pad = parseFloat(getComputedStyle(island).getPropertyValue('--island-pad')) || 6;
    island.style.setProperty('--island-sheet-width', `${Math.min(280, Math.max(200, Math.ceil(widest + 2 * pad)))}px`);
  }, [open, island, counts]);
  const total = Math.max(counts.wide, counts.narrow);
  if (open && total === 0) setOpen(false);
  return { counts, open, setOpen };
}

/** Keep the outgoing island mounted until its CSS exit finishes. */
export function WorkspaceActionOutlet({ className, label = 'Editor actions', placement = 'top' }: {
  className?: string; label?: string;
  /** `bottom` islands float at the foot of a panel; their sheet opens upward. */
  placement?: 'top' | 'bottom';
}) {
  const scope = useContext(ScopeContext);
  const store = useWorkspaceActionsStore();
  const selected = useSyncExternalStore(store.subscribe, () => store.select(scope.host), () => null);
  const notice = useSyncExternalStore(store.subscribe, () => store.navigationNotice(scope.host), () => '');
  const attempt = useSyncExternalStore(store.subscribe, () => store.navigationAttempt(scope.host), () => 0);
  const [retained, setRetained] = useState<symbol | null>(null);
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [island, setIsland] = useState<HTMLDivElement | null>(null);
  const sheetId = useId();
  const overflow = useIslandOverflow(host, island);
  if (selected && selected !== retained) setRetained(selected);
  const owner = selected ?? retained;
  // A blocked visit nudges the island and says why beside it. Attempts made
  // before this outlet mounted stay silent.
  const [seenAttempt, setSeenAttempt] = useState(attempt);
  const [nudge, setNudge] = useState(0);
  const [noticeShown, setNoticeShown] = useState(false);
  if (attempt !== seenAttempt) {
    setSeenAttempt(attempt);
    if (attempt) { setNudge(count => count + 1); setNoticeShown(true); overflow.setOpen(false); }
  }
  useEffect(() => {
    if (!noticeShown) return;
    const timer = setTimeout(() => setNoticeShown(false), 4000);
    return () => clearTimeout(timer);
  }, [noticeShown, seenAttempt]);
  const attach = useCallback((node: HTMLDivElement | null) => {
    setHost(node);
    if (node && owner) return store.attachOutlet(scope.host, { node, owner });
  }, [store, scope.host, owner]);
  const finishExit = () => { if (!selected) setRetained(null); };
  const attachMotion = useCallback((node: HTMLDivElement | null) => {
    if (!node || selected) return;
    const animations = node.getAnimations?.() ?? [];
    if (animations.length === 0) { setRetained(null); return; }
    let cancelled = false;
    void Promise.all(animations.map(animation => animation.finished.catch(() => {}))).then(() => { if (!cancelled) setRetained(null); });
    return () => { cancelled = true; };
  }, [selected]);
  const toggle = () => {
    const next = !overflow.open;
    overflow.setOpen(next);
    if (!next || !island) return;
    // CSS owns the breakpoint; it tells us which slots the sheet holds.
    const narrow = getComputedStyle(island).getPropertyValue('--island-mode').trim() === 'narrow';
    const inSheet = (slot?: string) => slot === 'overflow' || narrow && (slot === 'history' || slot === 'compact');
    requestAnimationFrame(() => {
      const first = [...island.querySelectorAll<HTMLElement>(actionable)].find(node => !node.matches(':disabled')
        && inSheet(node.closest<HTMLElement>('[data-island-slot]')?.dataset.islandSlot));
      first?.focus({ preventScroll: true });
    });
  };
  return owner ? <div ref={attachMotion} className={cn('workspace-action-outlet', className)} data-state={selected ? 'open' : 'closed'} inert={!selected}>
    <WorkspaceActionIsland ref={setIsland} id={sheetId} tabIndex={-1} role="group" aria-label={label} onAnimationEnd={event => { if (!event.animationName.startsWith('workspace-island-nudge')) finishExit(); }}
      data-placement={placement} data-nudge={nudge ? nudge % 2 ? 'odd' : 'even' : undefined} data-overflow-open={overflow.open} data-overflow-wide={overflow.counts.wide} data-overflow-narrow={overflow.counts.narrow}
      style={{ '--island-overflow-wide': overflow.counts.wide, '--island-overflow-narrow': overflow.counts.narrow } as CSSProperties}>
      {overflow.counts.narrow > 0 && <button type="button" className="workspace-action-more" aria-label="More actions" aria-expanded={overflow.open} aria-controls={sheetId} onClick={toggle}>
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
      </button>}
      <div ref={attach} className="workspace-action-island-commands" />
    </WorkspaceActionIsland>
    <span role="status" className="workspace-action-notice" data-placement={placement} data-visible={Boolean(notice) && noticeShown}>{notice}</span>
  </div> : null;
}

/** Duration of the Find-a-setting field pulse. */
export const WORKSPACE_FIELD_HIGHLIGHT_MS = 1000;
const focusableField = 'input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), select:not(:disabled), button:not(:disabled), [role="radio"][tabindex="0"], [role="switch"], [tabindex]:not([tabindex="-1"])';
/**
 * Bring a setting into view: scroll to it, focus its first control and pulse
 * its row once (a tonal halo, never a ring). Reduced motion: focus only.
 * `target` is the field's row or any element inside it; a `[data-setting-field]`
 * ancestor is used as the row when there is one.
 */
export function highlightWorkspaceField(target: Element | null | undefined): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const row = target.closest<HTMLElement>('[data-setting-field]') ?? target;
  const control = row.matches(focusableField) ? row : row.querySelector<HTMLElement>(focusableField);
  const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  row.scrollIntoView?.({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  control?.focus({ preventScroll: true });
  if (reduce) return true;
  row.removeAttribute('data-workspace-highlight');
  void row.offsetWidth; // restart the pulse on a repeated jump
  row.setAttribute('data-workspace-highlight', '');
  setTimeout(() => row.removeAttribute('data-workspace-highlight'), WORKSPACE_FIELD_HIGHLIGHT_MS);
  return true;
}
